<template>
    <div class="container mt-5 mb-5">
        <!-- <h2 class="text-center mb-4 text-primary fw-bold">Rev ID별 Quota Year 관리 (Vue 3)</h2> -->

        <div class="row justify-content-center">
            <div class="col-lg-10">
                <div class="card p-4">

                    <div class="mb-4 p-3 bg-light rounded-3">
                        <div class="row align-items-center">
                            <div class="col-md-5">
                                <label for="revIdSelect" class="form-label fw-bold">현재 Rev ID 선택:</label>
                                <select id="revIdSelect" class="form-select form-select-lg" v-model="currentRevId">
                                    <option v-for="revId in revIdList" :key="revId" :value="revId">{{ revId }}</option>
                                </select>
                            </div>
                            </div>
                    </div>

                    <h4 class="mb-3 text-secondary">Quota Year 목록 관리</h4>

                    <div id="selectedYearsArea">
                        <p class="fw-bold mb-2">선택된 연도 (드래그하여 다른 Rev ID로 이동 가능)</p>
                        <div id="selectedYears" class="d-flex flex-wrap storage-area"
                            @dragover.prevent="handleDragOver('current')" @dragleave="handleDragLeave('current')"
                            @drop="handleDrop(currentRevId, $event)" :class="{ 'drag-over': isDraggingOver.current }">
                            <span v-for="year in currentItem.quota_year" :key="year" class="year-tag" draggable="true"
                                @dragstart="handleDragStart(year, currentRevId, $event)"
                                @dragend="event => event.currentTarget.style.opacity = '1'">
                                {{ year }}
                                <i class="bi bi-x-circle-fill remove-btn" @click="removeYear(year, currentRevId)"></i>
                            </span>
                            <p v-if="currentItem.quota_year.length === 0" class="text-muted m-3">추가된 연도가 없습니다.</p>
                        </div>
                    </div>

                    <div class="input-group my-4">
                        <input type="number" v-model.trim="yearInput" class="form-control"
                            placeholder="추가할 연도 입력 (예: 2028)" min="1900" max="9999" @keyup.enter="handleInputAdd">
                        <button class="btn btn-primary" type="button" @click="handleInputAdd">
                            <i class="bi bi-plus-lg me-1"></i> 연도 추가
                        </button>
                    </div>

                    <h4 class="mb-3 text-secondary">연도 이동 대상 Rev ID</h4>
                    <div class="row">
                        <template v-for="revId in revIdList.filter(id => id !== currentRevId)" :key="revId">
                            <div class="col-md-6 mb-3">
                                <p class="fw-bold mb-2 badge bg-primary">{{ revId }}</p>
                                <div :id="`target${revId}`" class="d-flex flex-wrap storage-area"
                                    @dragover.prevent="handleDragOver(revId)" @dragleave="handleDragLeave(revId)"
                                    @drop="handleDrop(revId, $event)" :class="{ 'drag-over': isDraggingOver[revId] }">
                                    <span v-for="year in data[revId].quota_year" :key="year" class="year-tag" draggable="true"
                                        @dragstart="handleDragStart(year, revId, $event)"
                                        @dragend="event => event.currentTarget.style.opacity = '1'">

                                        {{ year }}
                                        <i class="bi bi-x-circle-fill remove-btn" @click="removeYear(year, revId)"></i>
                                    </span>
                                    <p v-if="data[revId].quota_year.length === 0" class="text-muted m-3">이동할 연도를 여기에
                                        드롭하세요.</p>
                                </div>
                            </div>
                        </template>
                        <div v-if="revIdList.length <= 1" class="col-12">
                            <p class="text-center text-muted m-3">이동 대상이 없습니다.</p>
                        </div>
                    </div>


                    <div class="d-flex justify-content-end mt-4">
                        <button class="btn btn-danger btn-lg me-3" @click="cancelChanges">
                            <i class="bi bi-x-circle me-1"></i> 취소
                        </button>
                        <button class="btn btn-success btn-lg" @click="saveChanges">
                            <i class="bi bi-save me-1"></i> 저장 (변경사항 반영)
                        </button>
                    </div>
                </div>
            </div>
        </div>

    </div>
</template>

<script setup>
import { ref, computed, onBeforeMount } from 'vue';
import { getProdRevIDYearList ,prodRevYearUpdate} from '@/api/prod'; // 실제 API 경로는 유지



// --- 1. 상태(State) 정의 ---

// data는 이제 rev_id를 키로 하는 객체 Map으로 사용됩니다. (초기에는 빈 객체)
const data = ref({}); 
const revIdList = computed(() => Object.keys(data.value)); // data Map의 키 목록

// 초기값 설정은 데이터 로딩 후에 이루어져야 합니다.
const currentRevId = ref('');
const yearInput = ref('');

// 드래그 앤 드롭 상태는 revIdList를 기반으로 동적으로 관리되어야 하므로, 
// revIdList의 변경에 의존하는 computed 속성으로 바꾸는 것이 좋습니다.
// 편의를 위해 일단 ref로 유지하고 로딩 후 초기화합니다.
const isDraggingOver = ref({}); 


// --- 데이터 로딩 로직 ---

onBeforeMount(async () => {
    // 💡 초기 데이터 로딩 함수 호출
    await getInitialData();
});

const getInitialData = async () => {
    try {
        // setIsLoading(true);
        
        // 1. API 호출 (실제 환경에서는 주석 해제)
        const res = await getProdRevIDYearList();
        const rawData = res.rtn;


        // 3. 배열 데이터를 내부적으로 사용하기 쉬운 객체 맵으로 변환 (핵심)
        const dataMap = rawData.reduce((map, item) => {
            const quotaYearStr = item.quota_year;
            // quota_year 문자열을 배열로 변환
            const quotaYearArray = quotaYearStr 
                ? quotaYearStr.split(',').filter(year => year.trim() !== '') 
                : [];
            
            map[item.rev_id] = {
                ...item,
                quota_year: quotaYearArray // 배열로 변환하여 저장
            };
            return map;
        }, {});

        // data ref에 객체 맵 저장 (반응성 확보)
        data.value = dataMap;

        // 4. 초기 currentRevId 설정
        const initialRevId = revIdList.value.includes('PV2025001') ? 'PV2025001' : revIdList.value[0];
        currentRevId.value = initialRevId;
        
        // 5. isDraggingOver 초기화
        const dragOverMap = { current: false };
        revIdList.value.forEach(id => { dragOverMap[id] = false; });
        isDraggingOver.value = dragOverMap;

    } catch (err) {
        console.error("데이터 로딩 중 오류 발생:", err);
    } finally {
        // setIsLoading(false);
    }
}


// --- 2. Computed 속성 (반응형 데이터) ---


const currentItem = computed(() => {
    // 💡 data.value가 비어있을 때 (로딩 중) 처리
    if (!currentRevId.value || !data.value[currentRevId.value]) {
        return { start_dt: 'N/A', end_dt: 'N/A', quota_year: [], rev_id: 'N/A' };
    }

    const item = data.value[currentRevId.value];
    
    // 원본 데이터를 복사하고 quota_year만 정렬하여 반환 (원본 데이터의 순서 변경 방지)
    const sortedQuotaYear = [...item.quota_year].sort((a, b) => parseInt(b) - parseInt(a));

    return {
        ...item,
        quota_year: sortedQuotaYear, // 정렬된 배열 사용
        // end_dt: displayEndDt // 주석 해제 시 위에서 displayEndDt 로직 필요
    };
});

// --- 3. 로직 함수(Methods) ---

// 연도 추가
const addYear = (quickYear = null) => {
    // currentRevId가 설정되지 않았으면 (로딩 중) 함수 종료
    if (!currentRevId.value) return; 

    const yearToAdd = quickYear || String(yearInput.value);
    const targetItem = data.value[currentRevId.value];

    if (!yearToAdd || yearToAdd.length !== 4 || isNaN(yearToAdd)) {
        alert("유효한 4자리 연도를 입력해 주세요.");
        return;
    }

    if (targetItem.quota_year.includes(yearToAdd)) {
        alert(`${yearToAdd}년은 이미 추가되어 있습니다.`);
        yearInput.value = ''; // 입력창 초기화
        return;
    }

    // 대상 배열에 연도 추가
    targetItem.quota_year.push(yearToAdd);
    yearInput.value = '';
};

const handleInputAdd = () => {
    const year = String(yearInput.value);

    if (!year || year.length !== 4 || isNaN(year)) {
        alert("유효한 4자리 연도를 입력해 주세요.");
        return;
    }

    addYear(year); // 순수 함수 호출
    yearInput.value = ''; // 입력창 초기화
};

// 연도 삭제 (어떤 Rev ID에서 삭제할지 명시)
const removeYear = (yearToRemove, revId) => {
    // revId가 data Map에 있는지 확인
    if (!data.value[revId]) return;

    const currentYears = data.value[revId].quota_year;
    data.value[revId].quota_year = currentYears.filter(y => y !== yearToRemove);
};

// 드래그 시작 (데이터 전송)
const handleDragStart = (year, sourceRevId, event) => {
    event.dataTransfer.setData('text/plain', JSON.stringify({ year, sourceRevId }));
    event.dataTransfer.effectAllowed = 'move';
    event.currentTarget.style.opacity = '0.4';

    // Drop 로직에서 요소를 찾기 위해 data 속성 추가
    event.currentTarget.dataset.year = year;
    event.currentTarget.dataset.sourceId = sourceRevId;
};

// 드래그 오버/리브 로직은 data.value의 구조 변경과 무관하므로 유지
const handleDragOver = (revIdKey) => {
    // ... (로직 유지)
    for (const key in isDraggingOver.value) {
        isDraggingOver.value[key] = false;
    }
    // 동적으로 생성된 revId에도 접근 가능해야 함
    if (isDraggingOver.value.hasOwnProperty(revIdKey)) {
        isDraggingOver.value[revIdKey] = true;
    } else {
        isDraggingOver.value.current = true; // current는 특별 처리
    }
};

const handleDragLeave = (revIdKey) => {
    // ... (로직 유지)
    isDraggingOver.value[revIdKey] = false;
};

// 드롭 처리 (연도 이동)
const handleDrop = (targetRevId, event) => {
    event.preventDefault();

    // 드래그 오버 상태 초기화
    for (const key in isDraggingOver.value) {
        isDraggingOver.value[key] = false;
    }

    let transferData;
    try {
        transferData = JSON.parse(event.dataTransfer.getData('text/plain'));
    } catch (e) {
        return; // 데이터가 올바르지 않으면 종료
    }
    
    const yearToMove = transferData.year;
    const sourceRevId = transferData.sourceRevId;

    // source와 target이 같으면 이동 불필요
    if (sourceRevId === targetRevId) return;

    // 타겟 목록에 연도가 이미 있는지 확인
    const targetYears = data.value[targetRevId].quota_year;
    if (targetYears.includes(yearToMove)) {
        alert(`${targetRevId}에는 ${yearToMove}년이 이미 존재합니다.`);
        // 드롭했지만 이동은 안 되므로 원래 드래그한 곳의 투명도를 복구
        const draggedElement = document.querySelector(`[draggable="true"][data-year="${yearToMove}"][data-source-id="${sourceRevId}"]`);
        if (draggedElement) {
            draggedElement.style.opacity = '1';
        }
        return;
    }

    // 1. 원본 목록에서 제거
    const sourceYears = data.value[sourceRevId].quota_year;
    data.value[sourceRevId].quota_year = sourceYears.filter(y => y !== yearToMove);

    // 2. 타겟 목록에 추가
    targetYears.push(yearToMove);
};

// 저장 (시뮬레이션)
const saveChanges = async() => {
    // DB API에 전송할 최종 데이터를 배열 형태로 재변환
    const finalDataArray = revIdList.value.map(revId => {
        const item = data.value[revId];
        return {
            rev_id: revId,
            start_dt: item.start_dt,
            end_dt: item.end_dt,
            quota_year: item.quota_year.join(',') 
        };
    });
    
    const res = await prodRevYearUpdate(finalDataArray)
    if(res.code == '0000'){
        alert('저장완료')
        globalThis.location.reload();
    }else{
        alert('저장에 실패 했습니다.')
    }
};

// 취소 (새로고침)
const cancelChanges = () => {
    if (confirm("모든 변경 사항을 취소하고 페이지를 새로고침하시겠습니까?")) {
        globalThis.location.reload(); 
    }
};
</script>

<style>
.card {
    border-radius: 12px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
}

.year-tag {
    background-color: #e9ecef;
    color: #495057;
    padding: .35rem .75rem;
    border-radius: 20px;
    font-weight: 500;
    display: flex;
    align-items: center;
    margin-right: 8px;
    margin-bottom: 8px;
    cursor: move;
    /* 드래그 가능함을 표시 */
    transition: all 0.2s;
    position: relative;
    -webkit-user-select: none;
    -ms-user-select: none;
    user-select: none;
}

.year-tag:hover {
    background-color: #dee2e6;
}

.remove-btn {
    position: absolute;
    top: -8px;
    right: -8px;
    cursor: pointer;
    margin-left: 8px;
    font-size: 1rem;
    color: #dc3545;
    transition: color 0.2s;
}

.remove-btn:hover {
    color: #c82333;
}

.storage-area {
    min-height: 100px;
    border: 2px dashed #ced4da;
    padding: 15px;
    border-radius: 8px;
    background-color: #fff;

}

.drag-over {
    background-color: #e6f3ff !important;
    border-color: #0d6efd !important;
}
</style>